import { Controller } from "@hotwired/stimulus"

// An Options card (review/_check), worked out afresh as a box changes: each box's tile in the colour of the option
// it goes under (dashed for a new one), the figure's options as saving would leave them, with how many listings
// each ("map"), what saving changes, by where it goes ("line": "Saving files 5kToys → new option Unpainted Ver."), and the button named for it: "Save 2 changes",
// green, or grey and off while nothing would change. A box's data-was is what it's under now ("" for none), its
// data-store whose listing it is; a page's options linked each on its own (Link each option) all count as changes.
export default class extends Controller {
  static targets = ["tile", "map", "line", "button", "label", "fixed"]
  static values = { looks: Object, on: String, off: String }

  connect() { this.count() }

  count() {
    const rows = this.boxes().map(select => ({ select, ...this.place(select) }))
    rows.forEach(row => this.paint(row))

    const changes = rows.filter(row => row.split || row.key !== (row.select.dataset.was || ""))
    const after = new Map()
    // A listing whose box can't change it; a page selling several stays put until its options are linked each on its own.
    this.fixedTargets.filter(span => !span.dataset.split || document.getElementById(span.dataset.split)?.hidden)
      .forEach(span => this.tally(after, span.dataset.name || "", false))
    rows.filter(row => !row.away).forEach(row => this.tally(after, row.key, row.fresh))
    this.mapTarget.replaceChildren(...[...after].map(([name, { n, fresh }]) => this.chip(name, n, fresh)))

    const by = new Map()
    changes.forEach(row => by.set(row.said, [...(by.get(row.said) || []), row.select.dataset.store]))
    this.lineTarget.textContent = changes.length
      ? `Saving files ${[...by].map(([said, stores]) => `${this.join(stores)} → ${said}`).join(" · ")}`
      : "Nothing to change yet: pick an option for a listing, or Leave as is."

    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = changes.length === 0
    this.buttonTarget.className = changes.length ? this.onValue : this.offValue
    this.labelTarget.textContent = changes.length ? `Save ${changes.length} ${changes.length === 1 ? "change" : "changes"}` : "Save options"
  }

  // The boxes that save: each listing's, and while shown, each option of a page linked separately.
  boxes() {
    return [...this.element.querySelectorAll("select[name^='versions['], select[name^='split_versions[']")].filter(select => !select.disabled)
  }

  // Where a box puts its listing: key (an option's name; "" for none), what the line calls it, and whether it's new or goes off the figure.
  place(select) {
    const v = select.value
    const split = select.name.startsWith("split_versions[")
    if (v === "__new__") {
      const typed = select.closest("label").querySelector("[data-new-version]").value.trim()
      return typed ? { key: typed, said: this.existing(typed) ? typed : `new option ${typed}`, fresh: !this.existing(typed), split }
                   : { key: "", said: "no option (no name typed)", split }
    }
    if (v === "") return { key: "", said: "no option", split }
    if (v === "__skip__") return { key: v, said: "off this figure", away: true, split }
    if (v.startsWith("__figure__:")) return { key: v, said: `another figure, ${select.selectedOptions[0].text.replace("Move to: ", "")}`, away: true, split }
    return { key: v, said: this.existing(v) ? v : `new option ${v}`, fresh: !this.existing(v), split }
  }

  existing(name) { return Object.hasOwn(this.looksValue.options, name) }

  look(row) {
    if (row.away) return this.looksValue.away
    if (row.key === "") return this.looksValue.none
    return this.looksValue.options[row.key] || this.looksValue.fresh
  }

  paint(row) {
    const tile = this.tileTargets.find(t => t.contains(row.select))
    if (!tile) return
    const look = this.look(row)
    tile.className = `${tile.dataset.base} ${look.tile}`
    const dot = tile.querySelector("[data-dot]")
    if (dot) dot.className = `${dot.dataset.base} ${look.dot}`
  }

  tally(after, name, fresh) {
    const seen = after.get(name) || { n: 0, fresh }
    after.set(name, { n: seen.n + 1, fresh: seen.fresh })
  }

  // One of the figure's options as saving leaves it, with its listings' count: its colour, a new one dashed.
  chip(name, n, fresh) {
    const look = name === "" ? this.looksValue.none : (this.looksValue.options[name] || this.looksValue.fresh)
    const chip = document.createElement("span")
    chip.className = `inline-flex items-center gap-1.5 rounded border-2 px-1.5 py-0.5 ${look.tile}`
    const dot = document.createElement("span")
    dot.className = `w-2 h-2 rounded-full flex-shrink-0 ${look.dot}`
    const label = document.createElement("span")
    label.textContent = `${name === "" ? "no option" : name}${fresh ? " (new)" : ""} · ${n}`
    chip.append(dot, label)
    return chip
  }

  join(list) {
    const names = [...new Set(list)]
    return names.length < 3 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`
  }
}
